import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Loader2, Zap } from 'lucide-react'
import { fetchWithAuth } from '@/lib/api'
import { testIds } from '@/lib/test-ids'
import { abbreviateWorkspaceName } from '@/lib/workspace-path-match'
import { skillMatchesSlashQuery } from '@/lib/compact-search-match'
import { cn } from '@/lib/utils'

export interface SlashSkillRow {
  id: string
  name: string
  description: string
  category: string
  triggerKeywords?: string[]
  /** From API: skill only applies when chat scope includes one of these workspace slugs */
  requiredWorkspaces?: string[]
}

/** Resolved from org list for workspace-colored skill labels */
export type SlashWorkspaceMeta = { name: string; color: string | null }

function slugFallbackLabel(slug: string): string {
  return slug
    .split('-')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

interface SlashCommandMenuProps {
  query: string
  position: { top: number; left: number }
  onSelect: (skill: SlashSkillRow) => void
  onClose: () => void
  /** Workspace slugs in current chat scope (from scope picker). Empty = no filter on skills. */
  allowedWorkspaceSlugs?: string[]
  /** slug -> display name + sidebar color (hex or CSS) */
  workspaceMetaBySlug?: Record<string, SlashWorkspaceMeta>
  /** Short summary of active chat workspaces (e.g. "Hop Studios, Personal") for menu header */
  chatScopeLabel?: string
  /** Current chat workspace org ids. Workspace skills are loaded only for these orgs. */
  organizationIds?: string[]
}

function SkillScopeRow({
  requiredSlugs,
  metaBySlug,
}: {
  requiredSlugs: string[]
  metaBySlug: Record<string, SlashWorkspaceMeta>
}) {
  if (!requiredSlugs.length) {
    return (
      <span className="text-[10px] font-normal normal-case tracking-normal text-muted-foreground/75">
        Any workspace
      </span>
    )
  }
  return (
    <span className="flex shrink-0 flex-nowrap items-center gap-1 justify-end">
      {requiredSlugs.map((slug) => {
        const meta = metaBySlug[slug]
        const fullName = meta?.name?.trim() || slugFallbackLabel(slug)
        const label = abbreviateWorkspaceName(fullName)
        const color = meta?.color?.trim() || null
        return (
          <span
            key={slug}
            title={fullName}
            className={cn(
              'inline-flex shrink-0 items-center rounded px-1.5 py-0.5 text-[10px] font-semibold leading-none border',
              !color && 'border-border/70 bg-muted/40 text-muted-foreground'
            )}
            style={
              color
                ? {
                    backgroundColor: `${color}1a`,
                    color,
                    borderColor: `${color}45`,
                  }
                : undefined
            }
          >
            {label}
          </span>
        )
      })}
    </span>
  )
}

export function SlashCommandMenu({
  query,
  position,
  onSelect,
  onClose,
  allowedWorkspaceSlugs = [],
  workspaceMetaBySlug = {},
  chatScopeLabel,
  organizationIds = [],
}: SlashCommandMenuProps) {
  const [skills, setSkills] = useState<SlashSkillRow[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const listRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const qs = `?organizationIds=${encodeURIComponent(organizationIds.filter(Boolean).join(','))}`
        const res = await fetchWithAuth(`/api/skills${qs}`)
        const data = await res.json()
        if (!cancelled && data.success && Array.isArray(data.skills)) {
          setSkills(
            data.skills.map((s: SlashSkillRow & { requiredWorkspaces?: string[] }) => ({
              ...s,
              requiredWorkspaces: Array.isArray(s.requiredWorkspaces) ? s.requiredWorkspaces : [],
            }))
          )
        }
      } catch (e) {
        console.error('[SlashCommandMenu] /api/skills failed', e)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [organizationIds.join('|')])

  const scopeFiltered = useMemo(() => {
    if (!allowedWorkspaceSlugs?.length) return skills
    const allowed = new Set(allowedWorkspaceSlugs.map((s) => s.toLowerCase()))
    return skills.filter((s) => {
      const req = s.requiredWorkspaces
      if (!req || req.length === 0) return true
      return req.some((slug) => allowed.has(String(slug).toLowerCase()))
    })
  }, [skills, allowedWorkspaceSlugs])

  const filtered = useMemo(() => {
    const q = query.trim()
    if (!q) return scopeFiltered
    return scopeFiltered.filter((s) => skillMatchesSlashQuery(s, q))
  }, [scopeFiltered, query])

  useEffect(() => {
    setSelectedIndex(0)
  }, [query, filtered.length])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedIndex((prev) => Math.min(prev + 1, Math.max(filtered.length - 1, 0)))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedIndex((prev) => Math.max(prev - 1, 0))
      } else if (e.key === 'Enter') {
        e.preventDefault()
        const item = filtered[selectedIndex]
        if (item) onSelect(item)
      } else if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [filtered, selectedIndex, onSelect, onClose])

  useEffect(() => {
    if (listRef.current && selectedIndex >= 0) {
      const el = listRef.current.children[selectedIndex] as HTMLElement
      el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }
  }, [selectedIndex])

  const dropdownWidth = Math.min(420, typeof window !== 'undefined' ? window.innerWidth - 20 : 420)
  const viewportRight = typeof window !== 'undefined' ? window.innerWidth : 1200
  const leftPosition = position.left
  const adjustedLeft =
    leftPosition + dropdownWidth > viewportRight
      ? Math.max(10, viewportRight - dropdownWidth - 10)
      : leftPosition
  const finalTop = Math.max(10, position.top)

  return createPortal(
    <div
      className="fixed z-[9999] max-w-[calc(100vw-20px)] overflow-hidden rounded-lg border border-border bg-popover shadow-2xl"
      data-autocomplete-menu="slash"
      data-testid={testIds.chatPane.slashCommandMenu}
      style={{
        top: `${finalTop}px`,
        left: `${adjustedLeft}px`,
        width: `${dropdownWidth}px`,
        transform: 'translateY(-100%)',
        marginBottom: '8px',
      }}
      role="listbox"
      aria-label="Skills"
    >
      <div className="px-3 py-1.5 text-xs border-b border-border space-y-0.5">
        <div className="font-medium text-muted-foreground flex items-center gap-1">
          <Zap className="w-3 h-3 shrink-0" />
          Skills
        </div>
        {chatScopeLabel ? (
          <div className="text-[10px] font-normal text-muted-foreground/90 leading-snug pl-4">
            Chat scope: {chatScopeLabel}
          </div>
        ) : null}
      </div>
      {loading && (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          <span className="ml-2 text-sm text-muted-foreground">Loading…</span>
        </div>
      )}
      {!loading && filtered.length === 0 && (
        <div className="px-3 py-4 text-sm text-muted-foreground">No matching skills.</div>
      )}
      {!loading && filtered.length > 0 && (
        <div ref={listRef} className="max-h-[300px] overflow-y-auto">
          {filtered.map((skill, index) => {
            const isSelected = index === selectedIndex
            const req = skill.requiredWorkspaces ?? []
            const gateSlugs = req.length ? req : []
            const accentColor =
              gateSlugs.length > 0
                ? workspaceMetaBySlug[gateSlugs[0]]?.color?.trim() || null
                : null
            return (
              <button
                key={skill.id}
                type="button"
                role="option"
                aria-selected={isSelected}
                data-testid={testIds.chatPane.slashSkillOption(skill.id)}
                className={cn(
                  'w-full text-left px-3 py-2.5 flex flex-col gap-0.5 border-b border-border/60 last:border-b-0 border-l-[3px]',
                  accentColor ? 'border-l-transparent' : 'border-l-muted-foreground/30',
                  isSelected ? 'bg-accent' : 'hover:bg-muted/60'
                )}
                style={accentColor ? { borderLeftColor: accentColor } : undefined}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onSelect(skill)}
              >
                <div className="text-sm font-medium">
                  {skill.name}
                  <span className="ml-1.5 font-normal text-muted-foreground">/{skill.id}</span>
                </div>
                <div className="text-xs text-muted-foreground line-clamp-2">{skill.description}</div>
                <div className="flex items-center justify-between gap-2 pt-0.5 min-w-0">
                  <span className="text-[10px] uppercase tracking-wide text-muted-foreground/80 shrink-0">
                    {skill.category}
                  </span>
                  <SkillScopeRow requiredSlugs={gateSlugs} metaBySlug={workspaceMetaBySlug} />
                </div>
              </button>
            )
          })}
        </div>
      )}
    </div>,
    document.body
  )
}
